/**
 * Access Hooks
 *
 * React hooks for access entry management (role/user -> policy mapping)
 * using TanStack Query
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiResponse, type ApiListResponse } from '@/lib/api';
import {
    useAccessControlStore,
    type Access,
} from '@/stores/access-control';
import { buildQueryString, buildFilterParams } from './query-utils';

// ============== Types ==============

export interface AccessInput {
    role?: string | null;
    user?: string | null;
    policy: string;
    sort?: number | null;
}

export interface AccessFilter {
    role?: string;
    user?: string;
    policy?: string;
}

// ============== Hooks ==============

/**
 * Fetch all access entries with optional filters
 * 
 * Uses the reference CMS query format:
 * /access?fields[]=policy.name&fields[]=policy.id&fields[]=id&fields[]=sort&filter[_and][0][role]=uuid
 */
export function useAccessEntries(filter?: AccessFilter) {
    const { setAccessEntries, setAccessLoading } = useAccessControlStore();

    return useQuery({
        queryKey: ['access', filter],
        queryFn: async () => {
            setAccessLoading(true);
            try {
                // Build query params in reference CMS format
                // Reference format: fields[]=policy.name&fields[]=policy.id&fields[]=id&fields[]=sort
                const queryParams: Record<string, unknown> = {
                    fields: ['policy.name', 'policy.id', 'policy.icon', 'id', 'sort', 'role', 'user'],
                    limit: -1,
                };

                // Add filter if provided
                if (filter && Object.keys(filter).length > 0) {
                    const filterParams = buildFilterParams(filter as Record<string, unknown>);
                    Object.assign(queryParams, filterParams);
                }

                const queryString = buildQueryString(queryParams);
                const response = await api.get<ApiListResponse<Access>>(`/access?${queryString}`);
                const entries = response.data || [];
                setAccessEntries(entries);
                return entries;
            } finally {
                setAccessLoading(false);
            }
        },
    });
}

/**
 * Fetch access entries for a specific role
 */
export function useRoleAccess(roleId: string | undefined) {
    return useAccessEntries(roleId ? { role: roleId } : undefined);
}

/**
 * Fetch access entries for a specific user
 */
export function useUserAccess(userId: string | undefined) {
    return useAccessEntries(userId ? { user: userId } : undefined);
}

/**
 * Fetch access entries for a specific policy
 */
export function usePolicyAccess(policyId: string | undefined) {
    return useAccessEntries(policyId ? { policy: policyId } : undefined);
}

/**
 * Create access entry
 */
export function useCreateAccess() {
    const queryClient = useQueryClient();
    const { addAccess } = useAccessControlStore();

    return useMutation({
        mutationFn: async (data: AccessInput) => {
            const response = await api.post<ApiResponse<Access>>('/access', data);
            return response.data;
        },
        onSuccess: (access) => {
            if (access) addAccess(access);
            queryClient.invalidateQueries({ queryKey: ['access'] });
        },
    });
}

/**
 * Update access entry
 */
export function useUpdateAccess() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<AccessInput> }) => {
            const response = await api.patch<ApiResponse<Access>>(`/access/${id}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['access'] });
        },
    });
}

/**
 * Delete access entry
 */
export function useDeleteAccess() {
    const queryClient = useQueryClient();
    const { removeAccess } = useAccessControlStore();

    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/access/${id}`);
            return id;
        },
        onSuccess: (id) => {
            removeAccess(id);
            queryClient.invalidateQueries({ queryKey: ['access'] });
        },
    });
}
